import { FC, ReactNode, MouseEvent } from 'react'
import { cn } from '@bem-react/classname'
import { Transition } from 'react-transition-group'

import './Modal.scss'
import { Memo } from '@/hoc/Memo'
import { Button } from '@/components/Button'
import { useTranslation } from 'react-i18next'

type TitlePositionType = 'center' | 'right' | 'left'
type ButtonPositionType = 'center' | 'right' | 'left'
type SizeModal = 'xl' | 'lg' | 'md' | 'sm'

const cnModal = cn('modal')

export interface ModalSchema {
  isOpen: boolean
  title?: string
  titleContent?: ReactNode
  isTitleBold?: boolean
  isConfirm?: boolean
  isInform?: boolean
  isButtonOutline?: boolean
  isButtonSmall?: boolean
  children?: ReactNode
  closeBtnText?: string
  yesBtnText?: string
  titlePosition?: TitlePositionType
  buttonPosition?: ButtonPositionType
  size?: SizeModal
  onClose?: () => void
  onConfirm?: () => void
  classTitle?: string
  isCloseIcon?: boolean
  noButton?: boolean
  isLoading?: boolean
  maxWidth?: number
  isDisabledConfirmButton?: boolean
  classNameModal?: string
  classNameOverlay?: string
  overlayTransparent?: boolean
}

export const Modal: FC<ModalSchema> = Memo(
  ({
    children,
    title,
    titleContent,
    isTitleBold,
    isOpen,
    isConfirm,
    isInform,
    isButtonOutline,
    isButtonSmall,
    titlePosition,
    buttonPosition,
    closeBtnText = 'Cancel',
    yesBtnText = 'Yes',
    size = 'sm',
    onClose,
    onConfirm,
    classTitle,
    isCloseIcon = false,
    noButton = false,
    isLoading,
    maxWidth,
    isDisabledConfirmButton,
    classNameModal,
    classNameOverlay,
    overlayTransparent,
  }: ModalSchema) => {
    // console.log('Modal isLoading', isLoading)

    const { t } = useTranslation('translation')
    const closeModal = (event: MouseEvent<HTMLDivElement>) => {
      event.stopPropagation()
      if (event.target === event.currentTarget && onClose && !isConfirm) {
        onClose()
      }
    }
    return (
      <Transition in={isOpen} timeout={500} unmountOnExit mountOnEnter>
        {state => (
          <div
            className={cnModal(
              'overlay',
              { transparent: !overlayTransparent },
              [state, classNameOverlay],
            )}
            onClick={e => closeModal(e)}
          >
            <div
              className={cnModal(
                {
                  xl: size === 'xl' && !maxWidth,
                  lg: size === 'lg' && !maxWidth,
                  md: size === 'md' && !maxWidth,
                  sm: size === 'sm' && !maxWidth,
                },
                [state, classNameModal],
              )}
              style={{
                maxWidth,
              }}
            >
              {isCloseIcon && (
                <button
                  onClick={() => onClose && onClose()}
                  className={cnModal('close')}
                >
                  <i className='fa-solid fa-xmark'></i>
                </button>
              )}
              {title && !titleContent && (
                <div
                  className={cnModal(
                    'title',
                    {
                      center: titlePosition === 'center',
                      right: titlePosition === 'right',
                      bold: isTitleBold,
                    },
                    [classTitle],
                  )}
                >
                  {t(title)}
                </div>
              )}
              {titleContent && !title && titleContent}
              {children}
              {isConfirm && !noButton && (
                <div
                  className={cnModal('btns', {
                    center: buttonPosition === 'center',
                    right: buttonPosition === 'right',
                  })}
                >
                  <Button
                    color={isButtonOutline ? 'outline-gray' : 'dark'}
                    small={isButtonSmall}
                    className={cnModal('btn')}
                    onClick={onClose}
                    isDisabled={isLoading}
                  >
                    {t(closeBtnText)}
                  </Button>
                  <Button
                    color={isButtonOutline ? 'outline-gray' : 'dark'}
                    small={isButtonSmall}
                    className={cnModal('btn')}
                    onClick={() => {
                      onConfirm && onConfirm()
                    }}
                    isLoading={isLoading}
                    isDisabled={isDisabledConfirmButton}
                    // isDefaultWidth
                  >
                    {t(yesBtnText)}
                  </Button>
                </div>
              )}

              {isInform && !isConfirm && !noButton && (
                <div
                  className={cnModal('btns', {
                    center: buttonPosition === 'center',
                    right: buttonPosition === 'right',
                  })}
                >
                  <Button
                    color={isButtonOutline ? 'outline-gray' : 'dark'}
                    small={isButtonSmall}
                    className={cnModal('btn')}
                    onClick={onClose}
                  >
                    {t(closeBtnText)}
                  </Button>
                </div>
              )}
            </div>
          </div>
        )}
      </Transition>
    )
  },
)
